
import { Link, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { CheckSquare, DollarSign, Eye, X } from "lucide-react";
import { useSellerNotifications } from "@/hooks/useSellerNotifications";
import { generateInitials } from "@/utils/userUtils";

const NotificationDropdown = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useSellerNotifications();
  const navigate = useNavigate();
  
  // Show only the most recent 5 notifications in dropdown
  const recentNotifications = notifications.slice(0, 5);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleNotificationClick = (notificationId: string) => {
    if (!notifications.find(n => n.id === notificationId)?.read_status) {
      markAsRead(notificationId);
    }
    navigate('/seller/notifications');
  };

  if (notifications.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p>No notifications yet</p>
        <p className="text-sm">You'll see property view notifications here</p>
      </div>
    );
  }

  return (
    <div className="max-h-96 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b">
        <h3 className="font-semibold">Notifications</h3>
        {unreadCount > 0 && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={markAllAsRead}
            className="text-xs"
          >
            Mark all read
          </Button>
        )}
      </div>

      {/* Notifications List */}
      <div className="divide-y">
        {recentNotifications.map((notification) => (
          <div
            key={notification.id}
            className={`p-3 hover:bg-muted/50 cursor-pointer transition-colors ${
              !notification.read_status ? 'bg-blue-50/50' : ''
            }`}
            onClick={() => handleNotificationClick(notification.id)}
          >
            <div className="flex items-start gap-3">
              {notification.type === 'task_reminder' ? (
                <div className="h-8 w-8 flex-shrink-0 rounded-full bg-amber-100 flex items-center justify-center">
                  <CheckSquare className="h-4 w-4 text-amber-600" />
                </div>
              ) : (
                <Avatar className="h-8 w-8 flex-shrink-0">
                  {notification.buyer?.profile_image_url && (
                    <AvatarImage 
                      src={notification.buyer.profile_image_url} 
                      alt={notification.buyer.full_name} 
                    />
                  )}
                  <AvatarFallback className="text-xs">
                    {generateInitials(notification.buyer?.full_name)}
                  </AvatarFallback>
                </Avatar>
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">
                    {notification.type === 'task_reminder' 
                      ? notification.metadata?.task_title || 'Task Reminder'
                      : notification.buyer?.full_name}
                  </p>
                  {!notification.read_status && (
                    <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                  {notification.type === 'task_reminder' ? (
                    <>
                      <CheckSquare className="h-3 w-3" />
                      {notification.metadata?.is_overdue ? 'Task overdue' : 'Task due today'} — {notification.metadata?.deal_name}
                    </>
                  ) : notification.type === 'property_offer' ? (
                    <>
                      <DollarSign className="h-3 w-3" />
                      Submitted an offer
                    </>
                  ) : notification.type === 'offer_withdrawn' ? (
                    <>
                      <X className="h-3 w-3" />
                      Withdrew their offer
                    </>
                  ) : (
                    <>
                      <Eye className="h-3 w-3" />
                      Viewed your property
                    </>
                  )}
                </p>
                
                {notification.type !== 'task_reminder' && (
                  <p className="text-xs font-medium truncate">
                    {notification.property?.address}, {notification.property?.city}
                  </p>
                )}
                
                <p className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="p-3 border-t">
        <Link to="/seller/notifications">
          <Button variant="ghost" size="sm" className="w-full justify-center">
            View all notifications
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default NotificationDropdown;
